import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCookieBite, FaCheck, FaTimes, FaShieldAlt } from 'react-icons/fa';

/**
 * Composant de consentement aux cookies
 * Affiche un popup pour informer l'utilisateur de la collecte de données
 * et obtenir son consentement
 */
const CookieConsent = ({ onAccept, onDecline }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà fait son choix
    const hasConsented = localStorage.getItem('cookieConsent');
    
    if (hasConsented === null) {
      // Afficher le popup après un court délai
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
    if (onAccept) onAccept();
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setIsVisible(false);
    if (onDecline) onDecline();
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-2xl border border-blue-100 overflow-hidden">
            <div className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="flex-shrink-0 bg-blue-100 p-4 rounded-full">
                  <FaCookieBite className="text-blue-600 text-3xl" />
                </div>
                
                <div className="flex-grow">
                  <h3 className="text-xl md:text-2xl font-bold text-blue-800 mb-2">
                    Nous respectons votre vie privée
                  </h3>
                  
                  <p className="text-blue-700 mb-4">
                    Ce site utilise des cookies et des technologies similaires pour suivre votre activité et améliorer votre expérience. 
                    En acceptant, vous nous permettez de collecter des données sur votre navigation pour personnaliser notre contenu et nos services.
                  </p>
                  
                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-100">
                          <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                            <FaShieldAlt className="mr-2" />
                            Données collectées
                          </h4>
                          <ul className="text-blue-700 space-y-2 ml-6 list-disc">
                            <li>Pages visitées et temps passé sur chaque page</li>
                            <li>Profondeur de défilement et sections consultées</li>
                            <li>Interactions avec les éléments importants (boutons, liens)</li>
                            <li>Comportement de sortie (quand et pourquoi vous quittez une page)</li>
                          </ul>
                          
                          <p className="mt-4 text-blue-700">
                            Ces données sont anonymes et nous aident à améliorer notre site et nos services.
                            Nous ne vendons pas vos données à des tiers.
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  <div className="flex flex-col sm:flex-row items-center gap-3 mt-2">
                    <button
                      onClick={toggleDetails}
                      className="text-blue-600 underline text-sm hover:text-blue-800 transition-colors"
                    >
                      {showDetails ? 'Masquer les détails' : 'Voir les détails'}
                    </button>
                    
                    <div className="flex-grow"></div>
                    
                    <button
                      onClick={handleDecline}
                      className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors flex items-center"
                    >
                      <FaTimes className="mr-2" />
                      Refuser
                    </button>
                    
                    <button
                      onClick={handleAccept}
                      className="px-6 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <FaCheck className="mr-2" />
                      Accepter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;